import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Grid,
  Paper,
  Skeleton,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import VideoLibraryIcon from "@mui/icons-material/VideoLibrary";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import api from "../../conf/api";
import { ApprovalStatus } from "./Enums/enums";
import CompilationCard from "./components/compilations/CompilationCard";
import CompilationsHero from "./components/compilations/CompilationsHero";
import CompilationsTable from "./components/compilations/CompilationsTable";
import CompilationsToolbar from "./components/compilations/CompilationsToolbar";
import CreateCompilationDialog from "./components/compilations/CreateCompilationDialog";
import EditCompilationDialog from "./components/compilations/EditCompilationDialog";
import WgidLookupBox from "./components/compilations/WgidLookupBox";
import {
  DeleteCompilationDialog,
  ManageWgidsDialog,
  PreviewVideoDialog,
  RejectCompilationDialog,
} from "./components/compilations/CompilationDialogs";

// null = "no status filter".
const STATUS_TABS = [
  { value: null, label: "All", statsKey: "total", color: "#475569" },
  { value: ApprovalStatus.PENDING_APPROVAL, label: "Pending", statsKey: "pending_approval", color: "#b45309" },
  { value: ApprovalStatus.APPROVED, label: "Approved", statsKey: "approved", color: "#15803d" },
  { value: ApprovalStatus.REJECTED, label: "Rejected", statsKey: "rejected", color: "#b91c1c" },
];

const VIEW_STORAGE_KEY = "cv.compilations.view";

const readStoredView = () => {
  try {
    const v = window.localStorage.getItem(VIEW_STORAGE_KEY);
    return v === "table" || v === "cards" ? v : "cards";
  } catch {
    return "cards";
  }
};

/**
 * Standalone Compilations management page.
 *
 * A compilation is created against a Brand from one submit: zero or more
 * WGIDs (each verified live), a video uploaded browser-direct to S3, and a
 * brand. CreatorVault assigns it a WCA id (WCAxxxxxx) on creation. The video
 * stays in a private staging area until an admin approves it, at which point
 * it's moved to its public mrss/{brand}/{WCA}/ path; rejecting deletes it.
 */
export default function CompilationsPreview() {
  const currentUser = useSelector((state) => state.user);
  const isAdmin = currentUser?.is_staff || currentUser?.is_superuser;

  const [brands, setBrands] = useState([]);
  const [selectedBrand, setSelectedBrand] = useState(null); // null = all brands
  const [statusFilter, setStatusFilter] = useState(null); // null = all statuses
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState(""); // debounced value actually sent to the API
  const [sort, setSort] = useState("NEWEST");
  const [view, setView] = useState(readStoredView);
  const [compilations, setCompilations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ total: 0, pending_approval: 0, approved: 0, rejected: 0, total_wgids: 0, brands_used: 0 });

  const [createOpen, setCreateOpen] = useState(false);
  const [managingCompilation, setManagingCompilation] = useState(null);
  const [deletingCompilation, setDeletingCompilation] = useState(null);
  const [previewingCompilation, setPreviewingCompilation] = useState(null);
  const [editingCompilation, setEditingCompilation] = useState(null);
  const [rejectingCompilation, setRejectingCompilation] = useState(null);

  const changeView = (next) => {
    setView(next);
    try {
      window.localStorage.setItem(VIEW_STORAGE_KEY, next);
    } catch {
      // Preference not being remembered isn't worth surfacing.
    }
  };

  useEffect(() => {
    api.get("/mrss/brands/?page_size=1000")
      .then((r) => setBrands(r.data.results || r.data))
      .catch(() => toast.error("Failed to load brands"));
  }, []);

  // Debounce free-text search so it doesn't fire a request on every keystroke.
  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput), 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  const fetchCompilations = useCallback(() => {
    setLoading(true);
    const params = { page_size: 500 };
    if (selectedBrand) params.brand = selectedBrand.name;
    if (statusFilter) params.status = statusFilter;
    if (search.trim()) params.search = search.trim();
    api.get("/mrss/compilations/", { params })
      .then((r) => setCompilations(r.data.results || r.data))
      .catch(() => toast.error("Failed to load compilations"))
      .finally(() => setLoading(false));
  }, [selectedBrand, statusFilter, search]);

  // Aggregate counts across ALL compilations for the current brand (ignoring
  // status/search/pagination) so the hero stats and status-tab counts stay
  // accurate even if more compilations exist than fit in one page.
  const fetchStats = useCallback(() => {
    const params = {};
    if (selectedBrand) params.brand = selectedBrand.name;
    api.get("/mrss/compilations/stats/", { params })
      .then((r) => setStats(r.data))
      .catch(() => {});
  }, [selectedBrand]);

  useEffect(() => { fetchCompilations(); }, [fetchCompilations]);
  useEffect(() => { fetchStats(); }, [fetchStats]);

  const refreshAll = () => { fetchCompilations(); fetchStats(); };

  const handleApprove = async (comp) => {
    try {
      await api.post(`/mrss/compilations/${comp.id}/approve/`);
      toast.success(`${comp.unique_key} approved — video moved to its public S3 path`);
      refreshAll();
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to approve");
    }
  };

  // Sorting is client-side: the endpoint already returns everything matching
  // the brand/status/search filters in one page (page_size 500).
  const sortedCompilations = useMemo(() => {
    const time = (c) => (c.created_date ? new Date(c.created_date).getTime() : 0);
    const out = [...compilations];
    if (sort === "OLDEST") return out.sort((a, b) => time(a) - time(b));
    if (sort === "TITLE") return out.sort((a, b) => (a.title || "").localeCompare(b.title || ""));
    if (sort === "WGIDS") return out.sort((a, b) => (b.wgids?.length || 0) - (a.wgids?.length || 0));
    return out.sort((a, b) => time(b) - time(a));
  }, [compilations, sort]);

  const hasFilters = Boolean(statusFilter || search.trim() || selectedBrand);
  const pendingCount = stats.pending_approval || 0;

  const cardHandlers = (c) => ({
    onPreview: () => setPreviewingCompilation(c),
    onManageWgids: () => setManagingCompilation(c),
    onEdit: () => setEditingCompilation(c),
    onDelete: () => setDeletingCompilation(c),
    onApprove: () => handleApprove(c),
    onReject: () => setRejectingCompilation(c),
  });

  return (
    // minWidth:0 keeps this page from widening its flex parent: anything wide
    // (the list view) has to scroll inside its own box, never push the page.
    <Box sx={{ p: { xs: 0, sm: 1, md: 2 }, minWidth: 0, maxWidth: "100%" }}>
      <CompilationsHero
        stats={stats}
        pendingCount={pendingCount}
        loading={loading}
        onRefresh={refreshAll}
        onCreate={() => setCreateOpen(true)}
      />

      <WgidLookupBox />

      {isAdmin && pendingCount > 0 && statusFilter !== ApprovalStatus.PENDING_APPROVAL && (
        <Box
          sx={{
            display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", mb: 2,
            px: { xs: 1.25, sm: 1.5 }, py: 1,
            borderRadius: 2, bgcolor: "#fffbeb", border: "1px solid #fde68a",
          }}
        >
          <HourglassEmptyIcon sx={{ fontSize: 18, color: "#b45309", flexShrink: 0 }} />
          <Typography variant="body2" sx={{ color: "#92400e", fontWeight: 600, fontSize: { xs: 12.5, sm: 14 } }}>
            {pendingCount} compilation{pendingCount !== 1 ? "s" : ""} waiting on your approval
          </Typography>
          <Chip
            size="small"
            clickable
            onClick={() => setStatusFilter(ApprovalStatus.PENDING_APPROVAL)}
            label="Review them"
            sx={{ height: 24, fontSize: 11, fontWeight: 700, color: "#92400e", bgcolor: "#fef3c7" }}
          />
        </Box>
      )}

      <CompilationsToolbar
        statusTabs={STATUS_TABS}
        statusFilter={statusFilter}
        onStatusChange={setStatusFilter}
        stats={stats}
        searchInput={searchInput}
        onSearchChange={setSearchInput}
        brands={brands}
        selectedBrand={selectedBrand}
        onBrandChange={setSelectedBrand}
        sort={sort}
        onSortChange={setSort}
        view={view}
        onViewChange={changeView}
        shownCount={sortedCompilations.length}
      />

      {loading ? (
        view === "cards" ? (
          <Grid container spacing={{ xs: 1.5, sm: 2 }}>
            {Array.from({ length: 6 }).map((_, i) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={i}>
                <Skeleton variant="rounded" height={320} sx={{ borderRadius: 3 }} />
              </Grid>
            ))}
          </Grid>
        ) : (
          <Box>
            {Array.from({ length: 8 }).map((_, i) => (
              <Skeleton key={i} variant="rounded" height={54} sx={{ mb: 0.75, borderRadius: 2 }} />
            ))}
          </Box>
        )
      ) : sortedCompilations.length === 0 ? (
        <Paper
          elevation={0}
          sx={{
            py: { xs: 5, sm: 8 }, px: { xs: 2, sm: 3 }, textAlign: "center",
            borderRadius: 3, border: "1px dashed #dbe3ec", bgcolor: "#fbfcfe",
          }}
        >
          <VideoLibraryIcon sx={{ fontSize: { xs: 40, sm: 48 }, color: "#cbd5e1", mb: 1 }} />
          <Typography variant="h6" fontWeight={700} sx={{ fontSize: { xs: 16, sm: 20 } }}>
            {hasFilters ? "No compilations match these filters" : "No compilations yet"}
          </Typography>
          <Typography variant="body2" color="text.secondary" mb={2.5} sx={{ maxWidth: 440, mx: "auto" }}>
            {hasFilters
              ? "Try clearing the brand, status, or search filters above."
              : "Create your first one — attach a few WGIDs and upload the assembled video."}
          </Typography>
          {hasFilters ? (
            <Button
              variant="outlined"
              color="inherit"
              onClick={() => { setStatusFilter(null); setSearchInput(""); setSelectedBrand(null); }}
              sx={{ borderRadius: 2, textTransform: "none", borderColor: "#e2e8f0", minHeight: 40 }}
            >
              Clear all filters
            </Button>
          ) : (
            <Button
              variant="contained"
              disableElevation
              startIcon={<AddIcon />}
              onClick={() => setCreateOpen(true)}
              sx={{ borderRadius: 2, textTransform: "none", minHeight: 40 }}
            >
              Create Compilation
            </Button>
          )}
        </Paper>
      ) : view === "cards" ? (
        <Grid container spacing={{ xs: 1.5, sm: 2 }}>
          {sortedCompilations.map((c) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={c.id}>
              <CompilationCard compilation={c} isAdmin={isAdmin} {...cardHandlers(c)} />
            </Grid>
          ))}
        </Grid>
      ) : (
        <CompilationsTable
          rows={sortedCompilations}
          isAdmin={isAdmin}
          onPreview={setPreviewingCompilation}
          onManageWgids={setManagingCompilation}
          onEdit={setEditingCompilation}
          onDelete={setDeletingCompilation}
          onApprove={handleApprove}
          onReject={setRejectingCompilation}
        />
      )}

      {!isAdmin && sortedCompilations.length > 0 && (
        <Alert severity="info" sx={{ mt: 2, borderRadius: 2 }}>
          Only admins can approve or reject compilations. You can still create, edit and preview them here.
        </Alert>
      )}

      <CreateCompilationDialog
        open={createOpen}
        brands={brands}
        defaultBrand={selectedBrand?.name || ""}
        onClose={() => setCreateOpen(false)}
        onCreated={() => { setCreateOpen(false); refreshAll(); }}
      />

      <ManageWgidsDialog
        open={!!managingCompilation}
        compilation={managingCompilation}
        onClose={() => { setManagingCompilation(null); refreshAll(); }}
        onChanged={refreshAll}
      />

      <DeleteCompilationDialog
        open={!!deletingCompilation}
        compilation={deletingCompilation}
        onClose={() => setDeletingCompilation(null)}
        onDeleted={() => { setDeletingCompilation(null); refreshAll(); }}
      />

      <PreviewVideoDialog
        open={!!previewingCompilation}
        compilation={previewingCompilation}
        onClose={() => setPreviewingCompilation(null)}
      />

      <EditCompilationDialog
        open={!!editingCompilation}
        compilation={editingCompilation}
        onClose={() => setEditingCompilation(null)}
        onSaved={() => { setEditingCompilation(null); refreshAll(); }}
      />

      <RejectCompilationDialog
        open={!!rejectingCompilation}
        compilation={rejectingCompilation}
        onClose={() => setRejectingCompilation(null)}
        onRejected={() => { setRejectingCompilation(null); refreshAll(); }}
      />
    </Box>
  );
}
